/**
 * Team Topology Designer Types
 * Based on the Team Topologies book by Matthew Skelton and Manuel Pais
 */

// Team types from Team Topologies
export type TeamType =
  | 'stream-aligned'
  | 'platform'
  | 'enabling'
  | 'complicated-subsystem';

// Interaction modes between teams
export type InteractionMode =
  | 'collaboration'
  | 'x-as-a-service'
  | 'facilitating';

// Cognitive load levels
export type CognitiveLoad = 'low' | 'medium' | 'high';

// Position on canvas
export interface Position {
  x: number;
  y: number;
}

// Team definition
export interface Team {
  id: string;
  name: string;
  type: TeamType;
  description?: string;
  position: Position;
  size?: number;  // Team member count
  cognitiveLoad?: CognitiveLoad;
  responsibilities?: string[];
  technologies?: string[];
  color?: string;  // Custom color override
}

// Interaction between teams
export interface TeamInteraction {
  id: string;
  sourceTeamId: string;
  targetTeamId: string;
  mode: InteractionMode;
  description?: string;
  temporary?: boolean;  // Collaboration should be temporary
  expectedEndDate?: string;
}

// Snapshot of topology at a point in time
export interface TopologySnapshot {
  id: string;
  name: string;
  date: string;  // ISO date string
  teams: Team[];
  interactions: TeamInteraction[];
  notes?: string;
}

// Full topology diagram with evolution
export interface TeamTopologyDiagram {
  id: string;
  name: string;
  description?: string;
  currentState: TopologySnapshot;
  targetStates: TopologySnapshot[];  // Evolution timeline
  createdAt: number;
  updatedAt: number;
}

// Canvas settings
export interface CanvasSettings {
  gridEnabled: boolean;
  gridSize: number;
  snapToGrid: boolean;
  showLabels: boolean;
  showInteractionLabels: boolean;
  zoom: number;
  panX: number;
  panY: number;
}

// Export options
export type ExportFormat = 'png' | 'svg' | 'json';

// Feature access for premium gating
export interface TopologyFeatures {
  canEdit: boolean;
  canSave: boolean;
  maxTeams: number;
  canExportPng: boolean;
  canExportSvg: boolean;
  canUseEvolutionTimeline: boolean;
  canUseConwayChecker: boolean;
}

// Team type configuration with colors and metadata
export const TEAM_TYPE_CONFIG: Record<TeamType, {
  label: string;
  description: string;
  color: string;
  icon: string;
  example: string;
}> = {
  'stream-aligned': {
    label: 'Stream-aligned',
    description: 'Aligned to a flow of work from a segment of the business domain',
    color: '#3B82F6',  // Blue
    icon: '🌊',
    example: 'Product teams, feature teams',
  },
  'platform': {
    label: 'Platform',
    description: 'Provides internal services to reduce cognitive load on stream-aligned teams',
    color: '#8B5CF6',  // Purple
    icon: '🏗️',
    example: 'Infrastructure team, developer experience team',
  },
  'enabling': {
    label: 'Enabling',
    description: 'Helps other teams acquire missing capabilities, usually temporarily',
    color: '#10B981',  // Green
    icon: '🎯',
    example: 'Architecture team, SRE coaching team',
  },
  'complicated-subsystem': {
    label: 'Complicated-subsystem',
    description: 'Works on a subsystem requiring specialist knowledge',
    color: '#F59E0B',  // Orange
    icon: '🔧',
    example: 'ML platform team, video encoding team',
  },
};

// Interaction mode configuration
export const INTERACTION_MODE_CONFIG: Record<InteractionMode, {
  label: string;
  description: string;
  color: string;
  lineStyle: 'solid' | 'dashed' | 'dotted';
  expectedDuration: string;
}> = {
  'collaboration': {
    label: 'Collaboration',
    description: 'Two teams work closely together for a defined period to discover new patterns',
    color: '#EC4899',  // Pink
    lineStyle: 'dashed',
    expectedDuration: 'Temporary (weeks to months)',
  },
  'x-as-a-service': {
    label: 'X-as-a-Service',
    description: 'One team provides a service that the other team consumes via an API',
    color: '#6366F1',  // Indigo
    lineStyle: 'solid',
    expectedDuration: 'Long-term',
  },
  'facilitating': {
    label: 'Facilitating',
    description: 'One team helps another team learn or adopt new approaches',
    color: '#14B8A6',  // Teal
    lineStyle: 'dotted',
    expectedDuration: 'Temporary (weeks to months)',
  },
};

// Cognitive load colors
export const COGNITIVE_LOAD_CONFIG: Record<CognitiveLoad, {
  label: string;
  color: string;
  description: string;
}> = {
  low: {
    label: 'Low',
    color: '#10B981',  // Green
    description: 'Team can easily handle their responsibilities',
  },
  medium: {
    label: 'Medium',
    color: '#F59E0B',  // Orange
    description: 'Team is at capacity but managing',
  },
  high: {
    label: 'High',
    color: '#EF4444',  // Red
    description: 'Team is overloaded - consider splitting or reducing scope',
  },
};

// Conway's Law alignment checker types
export interface ConwayAlignment {
  score: number;  // 0-100
  issues: ConwayIssue[];
  recommendations: string[];
}

export interface ConwayIssue {
  severity: 'warning' | 'error';
  teamId?: string;
  interactionId?: string;
  message: string;
  suggestion: string;
}

// Default canvas settings
export const DEFAULT_CANVAS_SETTINGS: CanvasSettings = {
  gridEnabled: true,
  gridSize: 20,
  snapToGrid: true,
  showLabels: true,
  showInteractionLabels: true,
  zoom: 1,
  panX: 0,
  panY: 0,
};

// Default team sizes
export const DEFAULT_TEAM_SIZE = {
  width: 180,
  height: 100,
};
